import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { Text } from '@sendoutcards/quantum-design-ui'
import { Icon } from 'src/design_system/atoms/icons/components/Icon'
import styles from './menu.module.scss'

export type MenuItem = {
  label: string
  detail?: string
  icon: 'promptings' | 'pricing' | 'login' | 'card' | 'cookie'
  onClick: () => void
  separatorBefore?: boolean
}

type MenuProps = {
  items: MenuItem[]
  variant?: 'dark' | 'light'
}

type MenuPosition = {
  top: number
  left: number
  width: number
}

const MENU_WIDTH = 260

const MenuIcon: React.FC<{ icon: MenuItem['icon'] }> = ({ icon }) => {
  switch (icon) {
    case 'pricing':
      return (
        <svg
          className={styles.strokeIcon}
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="M12 2v20" />
          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7H14a3.5 3.5 0 0 1 0 7H6" />
        </svg>
      )
    case 'login':
      return (
        <svg
          className={styles.strokeIcon}
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
          <path d="M10 17l5-5-5-5" />
          <path d="M15 12H3" />
        </svg>
      )
    case 'card':
      return (
        <svg
          className={styles.strokeIcon}
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="M22 2L11 13" />
          <path d="M22 2l-7 20-4-9-9-4 20-7z" />
        </svg>
      )
    case 'cookie':
      return (
        <svg
          className={styles.strokeIcon}
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5" />
          <path d="M8.5 8.5h.01" />
          <path d="M16 15.5h.01" />
          <path d="M12 12h.01" />
        </svg>
      )
    case 'promptings':
    default:
      return <Icon name="promptings_logo" color="currentColor" size={18} />
  }
}

export const Menu: React.FC<MenuProps> = ({ items, variant = 'dark' }) => {
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<MenuPosition | undefined>()

  useEffect(() => {
    if (!open) return

    const updatePosition = () => {
      const trigger = triggerRef.current
      if (!trigger) return

      const rect = trigger.getBoundingClientRect()
      const viewportPadding = 8
      const width = Math.min(
        MENU_WIDTH,
        window.innerWidth - viewportPadding * 2,
      )
      const left = Math.min(
        Math.max(rect.right - width, viewportPadding),
        window.innerWidth - width - viewportPadding,
      )

      setPosition({
        top: 80,
        left,
        width,
      })
    }

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    document.addEventListener('keydown', closeOnEscape)

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [open])

  const handleItemClick = (item: MenuItem) => {
    setOpen(false)
    item.onClick()
  }

  return (
    <div className={styles.menuRoot}>
      <button
        ref={triggerRef}
        className={
          open ? `${styles.trigger} ${styles.triggerOpen}` : styles.trigger
        }
        type="button"
        aria-label={open ? 'Close navigation menu' : 'Open navigation menu'}
        aria-expanded={open}
        onClick={() => setOpen(current => !current)}
      >
        <span aria-hidden="true" />
        <span aria-hidden="true" />
        <span aria-hidden="true" />
      </button>

      {open &&
        position &&
        ReactDOM.createPortal(
          <>
            <div
              className={styles.overlay}
              onMouseDown={() => setOpen(false)}
            />
            <div
              className={`${styles.panel} ${
                variant === 'light' ? styles.lightPanel : styles.darkPanel
              }`}
              role="menu"
              style={{
                top: position.top,
                left: position.left,
                width: position.width,
              }}
              onMouseDown={event => event.stopPropagation()}
            >
              <div className={styles.panelInner}>
                {items.map(item => (
                  <React.Fragment key={item.label}>
                    {item.separatorBefore && (
                      <div className={styles.separator} role="separator" />
                    )}
                    <button
                      className={styles.menuItem}
                      type="button"
                      role="menuitem"
                      onClick={() => handleItemClick(item)}
                    >
                      <span
                        className={`${styles.itemIcon} ${
                          item.icon === 'promptings'
                            ? styles.promptingsIcon
                            : ''
                        }`}
                        aria-hidden="true"
                      >
                        <MenuIcon icon={item.icon} />
                      </span>
                      <Text
                        type="body"
                        weight="bold"
                        style={{ color: 'inherit', lineHeight: 1 }}
                      >
                        {item.label}
                      </Text>
                    </button>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  )
}
